import { useState, useEffect, useRef } from 'react';
import { ScrollReveal } from './ScrollReveal';

const CMD_IMG = 'https://static.prod-images.emergentagent.com/jobs/cf608264-414a-47a4-aa49-755218a60bb9/images/39d3b47528c28509e96d2825f2df1e2f276cc0ebebf4a057f3183faff78595d0.png';

const ALL_ACTIVITIES = [
  { agent: 'Executive Agent', action: 'Board briefing prepared — awaiting approval', status: 'pending' },
  { agent: 'Legal Agent', action: 'Contract review completed — NDA flagged for review', status: 'complete' },
  { agent: 'Sales Agent', action: 'Lead enrichment: Fortune 500 prospect identified', status: 'complete' },
  { agent: 'Research Agent', action: 'Competitor analysis: Q4 market report structured', status: 'complete' },
  { agent: 'Operations Agent', action: 'Workflow bottleneck detected — escalation routed', status: 'alert' },
  { agent: 'Finance Agent', action: 'Invoice anomaly detected — flagged for review', status: 'alert' },
  { agent: 'Marketing Agent', action: 'Campaign copy drafted — 3 variants ready', status: 'complete' },
  { agent: 'Customer Service Agent', action: 'SLA risk: 2 tickets approaching deadline', status: 'alert' },
  { agent: 'Procurement Agent', action: 'Vendor comparison complete — 3 suppliers ranked', status: 'complete' },
  { agent: 'Private Office Agent', action: 'Travel briefing pack assembled for London meeting', status: 'complete' },
  { agent: 'Executive Agent', action: 'Decision queue updated — 4 items pending', status: 'pending' },
  { agent: 'Legal Agent', action: 'Due diligence checklist: 2 documents missing', status: 'alert' },
  { agent: 'Sales Agent', action: 'Follow-up drafts prepared for 8 warm leads', status: 'complete' },
  { agent: 'Research Agent', action: 'Market intelligence brief ready for strategy meeting', status: 'complete' },
  { agent: 'Operations Agent', action: 'Cross-department task sync completed', status: 'complete' },
  { agent: 'Finance Agent', action: 'Cashflow snapshot prepared for management review', status: 'complete' },
  { agent: 'Marketing Agent', action: 'Competitor messaging analysis: 5 brands compared', status: 'complete' },
  { agent: 'Executive Agent', action: 'Weekly executive summary compiled from all departments', status: 'complete' },
  { agent: 'Legal Agent', action: 'Standard terms deviation found in supplier agreement', status: 'alert' },
  { agent: 'Private Office Agent', action: 'Multi-entity priority overview consolidated', status: 'complete' },
];

const SIDE_ALERTS = [
  'Briefing Ready — Executive review',
  'Contract flagged — Clause risk detected',
  'Warm lead losing momentum — Sales',
  'Approval needed — Finance transaction',
  'Research summary ready — Strategy team',
  'SLA risk — Customer ticket #4821',
  'Vendor contract expiring — 14 days',
  'Invoice anomaly — Procurement review',
  'Meeting prep complete — Board call',
  'Workflow delay — Operations team',
];

export default function CommandCenter() {
  const [activities, setActivities] = useState(ALL_ACTIVITIES.slice(0, 6));
  const [stats, setStats] = useState({ agents: 12, tasks: 47, approvals: 3, memory: 1247 });
  const [alertIdx, setAlertIdx] = useState(0);
  const tickRef = useRef(0);

  useEffect(() => {
    const interval = setInterval(() => {
      tickRef.current = (tickRef.current + 1) % ALL_ACTIVITIES.length;
      setActivities(prev => {
        const next = [...prev];
        next.pop();
        next.unshift(ALL_ACTIVITIES[(tickRef.current) % ALL_ACTIVITIES.length]);
        return next;
      });
      setStats(prev => ({
        agents: 10 + Math.floor(Math.random() * 3),
        tasks: 40 + Math.floor(Math.random() * 20),
        approvals: 1 + Math.floor(Math.random() * 5),
        memory: prev.memory + Math.floor(Math.random() * 5),
      }));
      setAlertIdx(prev => (prev + 1) % SIDE_ALERTS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const timeAgo = (i) => {
    const mins = [1, 2, 4, 7, 11, 16];
    return `${mins[i] || i + 1}m ago`;
  };

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="command-center-section">
      <div className="absolute inset-0">
        <img src={CMD_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-8" loading="lazy" />
        <div className="absolute inset-0 bg-aurocle-base/95" />
      </div>
      <div className="absolute inset-0 moving-bg" style={{background:'linear-gradient(135deg, rgba(212,175,55,0.02) 0%, transparent 30%, rgba(212,175,55,0.01) 60%, transparent 100%)'}} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <div className="overline mb-4">Command Center</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
              Your operational intelligence layer.
            </h2>
            <p className="text-base text-aurocle-platinum-muted max-w-2xl mx-auto">
              A unified view of your entire private workforce — every agent, every task, every decision — visible, controlled, and auditable.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={150}>
          <div className="glass-card border-aurocle-gold/10 overflow-hidden relative flash-sweep">
            <div className="bg-aurocle-surface/80 p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 breathe" />
                  <span className="text-xs font-mono text-aurocle-gold uppercase tracking-wider">Live Operations</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-[10px] font-mono text-aurocle-gold/40 ticker-item" key={alertIdx}>
                    {SIDE_ALERTS[alertIdx]}
                  </div>
                  <span className="text-xs font-mono text-aurocle-text-secondary">Aurocle Agent Command Center</span>
                </div>
              </div>

              {/* Stats with animated numbers */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Active Agents', value: stats.agents, sub: 'Currently operational' },
                  { label: 'Tasks in Queue', value: stats.tasks, sub: 'Across departments' },
                  { label: 'Approvals Pending', value: stats.approvals, sub: 'Awaiting authorization' },
                  { label: 'Memory Events', value: stats.memory.toLocaleString(), sub: 'Context preserved' },
                ].map((p, i) => (
                  <div key={i} className="p-4 bg-aurocle-base/50 border border-white/5 relative overflow-hidden agent-working" data-testid={`cmd-stat-${i}`}>
                    <div className="relative z-10">
                      <div className="text-xs font-mono text-aurocle-text-secondary mb-1">{p.label}</div>
                      <div className="text-2xl font-heading font-medium text-aurocle-text transition-all duration-500">{p.value}</div>
                      <div className="text-xs text-aurocle-platinum-muted mt-1">{p.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Live Activity Feed */}
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <div className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-4 flex items-center gap-2">
                    Recent Agent Activity
                    <span className="w-1.5 h-1.5 rounded-full bg-aurocle-gold breathe" />
                  </div>
                  <div className="space-y-2">
                    {activities.map((a, i) => (
                      <div key={`${a.agent}-${a.action}-${i}`}
                        className="flex items-center gap-4 p-3 bg-aurocle-base/30 border border-white/3 hover:border-aurocle-gold/15 transition-all ticker-item"
                        data-testid={`activity-${i}`}>
                        <div className={`w-2 h-2 rounded-full shrink-0 ${
                          a.status === 'complete' ? 'bg-emerald-400' : a.status === 'pending' ? 'bg-amber-400 breathe' : 'bg-red-400 breathe'
                        }`} />
                        <span className="text-xs font-mono text-aurocle-gold/70 w-36 shrink-0">{a.agent}</span>
                        <span className="text-xs text-aurocle-text flex-1">{a.action}</span>
                        <span className="text-xs font-mono text-aurocle-text-secondary shrink-0">{timeAgo(i)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Side panels */}
                <div className="space-y-3">
                  <div className="p-4 bg-aurocle-base/40 border border-aurocle-gold/10">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-aurocle-gold/60 mb-3">Executive Queue</div>
                    <div className="space-y-2">
                      {['Board briefing review', 'Q4 strategy approval', 'Vendor selection decision'].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-aurocle-text">
                          <div className="w-1 h-1 rounded-full bg-amber-400" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 bg-aurocle-base/40 border border-white/5">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">Workflow Health</div>
                    <div className="space-y-2">
                      {[
                        { dept: 'Legal', pct: 94 },
                        { dept: 'Sales', pct: 87 },
                        { dept: 'Operations', pct: 91 },
                      ].map((d, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-[10px] font-mono text-aurocle-text-secondary w-16">{d.dept}</span>
                          <div className="flex-1 h-1 bg-aurocle-base rounded-full overflow-hidden">
                            <div className="h-full bg-aurocle-gold/40 rounded-full transition-all duration-1000" style={{width:`${d.pct}%`}} />
                          </div>
                          <span className="text-[10px] font-mono text-aurocle-gold/60">{d.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 bg-aurocle-base/40 border border-white/5">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-aurocle-text-secondary mb-2">Memory Layer</div>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-aurocle-gold/50 breathe" />
                      <span className="text-[10px] text-aurocle-platinum-muted">Active — {stats.memory.toLocaleString()} events preserved</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={300}>
          <div className="mt-8 text-center">
            <a href="#contact" className="btn-gold cta-burning" data-testid="cmd-cta">
              Design Your Workforce
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
